import React, { useEffect, useRef, useState } from "react";
import { IoWarning, IoDownloadOutline } from "react-icons/io5";
import { BsPlay, BsPause } from "react-icons/bs";
import { ScaleLoader } from "react-spinners";

export type ChatProp = {
	message: string;
	_id: string;
	sender: string;
	createdAt: string;
	message_read: boolean;
	message_type: string;
};
type CardProp = {
	bookingDetails: BookingsTypes;
	messages: ChatProp[];
};

const getFileNameFromUrl = (url: string) => {
	try {
		const urlParts = url.split("/");
		return urlParts[urlParts.length - 1];
	} catch (error) {
		return "file";
	}
};
const convertLinksToAnchors = (text: string) => {
	const urlRegex = /(https?:\/\/[^\s]+)/g;
	return text.replace(
		urlRegex,
		(url) =>
			`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`
	);
};

const renderMessageContent = (
	messageObj: any,
	audioRefs: any,
	playingAudio: any,
	setPlayingAudio: any,
	audioProgress: any,
	setAudioProgress: any,
	isVendor: boolean
) => {
	const { message_type, message, url, _id, sender } = messageObj;
	if (!message_type || message_type === "text") {
		return (
			<div
				className='text-[15px] leading-6 break-words'
				dangerouslySetInnerHTML={{ __html: convertLinksToAnchors(message) }}
			/>
		);
	}
	if (message_type === "image") {
		return <img src={url} alt='Image' className='w-60 rounded-xl' />;
	}
	if (message_type === "audio") {
		return (
			<div
				className={`flex items-center gap-2 w-full max-w-[320px] ${
					isVendor ? "bg-[#E9D6F7]" : "bg-[#F2F2F2]"
				} rounded-xl px-3 py-2`}
			>
				<button
					className={`flex items-center justify-center rounded-full w-8 h-8 ${
						isVendor
							? "bg-purple-600 text-white"
							: "bg-gray-300 text-purple-600"
					}`}
					onClick={() => {
						if (playingAudio === _id) {
							audioRefs.current[_id]?.pause();
							setPlayingAudio(null);
						} else {
							if (playingAudio && audioRefs.current[playingAudio]) {
								audioRefs.current[playingAudio]?.pause();
							}
							if (!audioRefs.current[_id]) {
								audioRefs.current[_id] = new Audio(url);
								audioRefs.current[_id].addEventListener("timeupdate", () => {
									const audio = audioRefs.current[_id];
									const progress = (audio.currentTime / audio.duration) * 100;
									setAudioProgress((prev: any) => ({
										...prev,
										[_id]: progress,
									}));
								});
								audioRefs.current[_id].addEventListener("ended", () => {
									setPlayingAudio(null);
									setAudioProgress((prev: any) => ({ ...prev, [_id]: 0 }));
								});
							}
							audioRefs.current[_id].play();
							setPlayingAudio(_id);
						}
					}}
				>
					{playingAudio === _id ? <BsPause size={18} /> : <BsPlay size={18} />}
				</button>
				<div className='flex-1 h-1.5 bg-gray-300 rounded-full relative'>
					<div
						className={`h-1.5 rounded-full ${
							isVendor ? "bg-purple-600" : "bg-gray-400"
						}`}
						style={{ width: `${audioProgress[_id] || 0}%` }}
					></div>
				</div>
				<button
					className='ml-2 text-gray-400 hover:text-purple-600'
					onClick={() => {
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", getFileNameFromUrl(url));
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
				>
					<IoDownloadOutline size={18} />
				</button>
			</div>
		);
	}
	// Other file types
	return (
		<a
			href={url}
			target='_blank'
			rel='noopener noreferrer'
			className='flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200'
		>
			<span className='text-lg'>📄</span>
			<span>{getFileNameFromUrl(url)}</span>
			<button
				className='ml-auto text-gray-400 hover:text-purple-600'
				onClick={(e) => {
					e.preventDefault();
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", getFileNameFromUrl(url));
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}}
			>
				<IoDownloadOutline size={18} />
			</button>
		</a>
	);
};

const UserArtisanChart = ({ bookingDetails, messages }: CardProp) => {
	const messageRef = useRef<HTMLDivElement | null>(null);
	const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
	const [playingAudio, setPlayingAudio] = useState<string | null>(null);
	const [audioProgress, setAudioProgress] = useState<{ [key: string]: number }>(
		{}
	);

	useEffect(() => {
		messageRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	const customerImg = bookingDetails?.user_meta?.display_picture;
	const vendorImg = bookingDetails?.artisan_meta?.display_picture;
	const messengers = {
		user_meta: bookingDetails?.user_meta || {},
		artisan_meta: bookingDetails?.artisan_meta || {},
	};

	console.log(messengers);
	const chatTitleText = `${messengers.user_meta.first_name} & ${messengers.artisan_meta.first_name}'s Chat`;

	return (
		<div className='bg-[#F7F6FA] flex flex-col rounded-2xl h-full'>
			{/* Top bar */}
			<div className='rounded-t-2xl bg-[#D1B3F5] text-center py-3 text-[17px] font-medium text-[#4D4D4D]'>
				You are viewing customer and vendor chat
			</div>
			{/* Header */}
			<div className='flex items-center justify-between px-8 py-4 bg-white border-b border-[#E9D6F7]'>
				<div className='flex items-center gap-2'>
					<img
						src={customerImg}
						alt='customer'
						className='w-12 h-12 rounded-full border-2 border-white -mr-3 z-10 object-cover'
					/>
					<img
						src={vendorImg}
						alt='vendor'
						className='w-12 h-12 rounded-full border-2 border-white z-0 object-cover'
					/>
					<span className='ml-3 font-semibold text-[#4D4D4D] text-lg'>
						{chatTitleText}
					</span>
				</div>
			</div>
			{/* Tab bar */}
			<div className='flex w-full bg-[#E9D6F7] text-[#7607BD] font-semibold text-base'>
				<div className='flex-1 text-center py-2'>Customer</div>
				<div className='flex-1 text-center py-2 border-b-2 border-transparent'>
					Vendor
				</div>
			</div>
			{/* Chat Area */}
			<div className='flex-1 overflow-y-auto px-8 py-8 space-y-8'>
				{messages && messages.length > 0 ? (
					<div className='flex flex-col gap-8'>
						{messages.map((message: any, idx: number) => {
							const isVendor = message?.sender === "artisan";
							const avatar = isVendor ? vendorImg : customerImg;
							const name = isVendor
								? messengers.artisan_meta.first_name
								: messengers.user_meta.first_name;
							const bubbleColor = isVendor
								? "bg-[#E9D6F7] text-[#4D4D4D]"
								: "bg-[#F2F2F2] text-[#4D4D4D]";
							const bubbleAlign = isVendor ? "items-end" : "items-start";
							const flexDir = isVendor ? "flex-row-reverse" : "flex-row";
							return (
								<div
									key={message?._id || idx}
									className={`flex ${flexDir} w-full gap-3`}
								>
									{!isVendor && (
										<img
											src={avatar}
											alt='avatar'
											className='w-10 h-10 rounded-full object-cover self-end'
										/>
									)}
									<div className={`flex flex-col max-w-[70%] ${bubbleAlign}`}>
										<span
											className='font-semibold text-[15px] mb-1'
											style={{ color: isVendor ? "#7607BD" : "#4D4D4D" }}
										>
											{name}
										</span>
										<div
											className={`rounded-2xl px-5 py-3 ${bubbleColor} mb-1`}
											style={{ wordBreak: "break-word" }}
										>
											{renderMessageContent(
												message,
												audioRefs,
												playingAudio,
												setPlayingAudio,
												audioProgress,
												setAudioProgress,
												isVendor
											)}
										</div>
										<span
											className={`text-xs mt-1 ${
												isVendor ? "text-right" : "text-left"
											} text-[#999]`}
										>
											{message.createdAt}
										</span>
									</div>
									{isVendor && (
										<img
											src={avatar}
											alt='avatar'
											className='w-10 h-10 rounded-full object-cover self-end'
										/>
									)}
								</div>
							);
						})}
					</div>
				) : (
					<div className='flex justify-center items-center py-10 px-5'>
						<span className='text-sm px-5 py-3 flex items-center leading-5 rounded-lg bg-gray-50 text-gray-500'>
							<IoWarning size={20} /> There are currently no available chat
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default UserArtisanChart;
